import { useEffect, useState, type FunctionComponent } from "react";
import { Outlet, useParams } from "react-router-dom";
import Spinner from "../../../components/spinner.components";
import {
  CampaignStatus,
  type ICampaign,
} from "../../../types/models/campaign.models.types";
import { useToast } from "../../../hooks/use-toast";
import { ActiveCampaignBadge } from "../../../components/badges.components";
import ClockIcon from "../../../assets/svgs/common/clock.svg";
import millify from "millify";
import { formatDate, getBarColor, handleHttpError } from "../../../utils/utils";
import AddImageIcon from "../../../assets/svgs/common/add-image.svg";
import type { ICampaignMetricMinimal } from "../../../types/models/campaign-metric.types";
import { Linechart } from "../../../components/charts/line.chart";
import { Barchart } from "../../../components/charts/bar.chart";
import StatsHomeDashboardBrandPartial from "../../../partials/brand/home/stats-home-dashboard.brand.partials";
import { useGetCampaignByIdEphemeralHttp } from "../../../hooks/http/ephemeral/campaigns/get-campaign-by-id.ephemeral.http";
import {
  type ICampaignMetricsEmphemeral,
  useGetCampaignMetricsEmphemeralHttp,
} from "../../../hooks/http/ephemeral/campaigns/get-campaign-metrics.ephemeral.http";

export const CampaignHomeBrandScreen: FunctionComponent = () => {
  const [campaignData, setCampaignData] = useState<ICampaign>();
  const [campaignMetrics, setCampaignMetrics] =
    useState<ICampaignMetricsEmphemeral>();
  const { fetching: fetchingCampaign, execute: getCampaignByIdHttp } =
    useGetCampaignByIdEphemeralHttp();
  const { fetching: fetchingCampaignMetrics, execute: getCampaignMetricsHttp } =
    useGetCampaignMetricsEmphemeralHttp();

  const { campaignId } = useParams();
  const { toast } = useToast();

  async function getCampaignMetrics() {
    if (!campaignId) {
      return;
    }
    const response = await getCampaignMetricsHttp({
      body: null,
      pathParams: { id: campaignId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setCampaignMetrics(response.data.metrics);
  }

  async function getCampaignById() {
    const response = await getCampaignByIdHttp({
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setCampaignData(response.data.campaign);
  }

  const AverageViewsPerPostChartData = [
    {
      category: "Views",
      value: campaignMetrics?.averageViewsPerPost ?? 0,
      fill: getBarColor(1),
    },
    {
      category: "Likes",
      value: campaignMetrics?.averageLikesPerPost ?? 0,
      fill: getBarColor(2),
    },
    {
      category: "Comments",
      value: campaignMetrics?.averageCommentsPerPost ?? 0,
      fill: getBarColor(0),
    },
    {
      category: "Shares",
      value: campaignMetrics?.averageSharesPerPost ?? 0,
      fill: getBarColor(3),
    },
  ];

  const ApprovedPostsChartData = [
    {
      category: "Total Posts",
      value: campaignMetrics?.totalPosts ?? 0,
      fill: getBarColor(0),
    },
    {
      category: "Approved Posts",
      value: campaignMetrics?.approvedPosts ?? 0,
      fill: getBarColor(1),
    },
    {
      category: "Rejected Posts",
      value: campaignMetrics?.rejectedPosts ?? 0,
      fill: getBarColor(2),
    },
  ];
  const PostPerDayChartData = [
    {
      category: "Total Posts",
      value: millify(campaignMetrics?.totalPosts ?? 0),
    },
  ];

  const TotalViewsChartData = [
    {
      category: "Total views",
      value: millify(campaignMetrics?.totalViews ?? 0),
    },
    {
      category: "Cost per view",
      value: `₹ ${(campaignMetrics?.costPerView ?? 0).toFixed(3)}`,
    },
  ];

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getCampaignById();
    getCampaignMetrics();
  }, [campaignId]);

  const startDate = campaignData?.startDate
    ? new Date(campaignData.startDate)
    : null;
  const endDate = campaignData?.endDate ? new Date(campaignData.endDate) : null;

  return (
    <div className="flex flex-col gap-8 pt-8 md:pt-16 w-full min-w-0">
      {fetchingCampaign ? (
        <div className="flex justify-center items-center w-full h-96">
          <Spinner />
        </div>
      ) : (
        <div className="flex lg:flex-row flex-col gap-6">
          {campaignData?.coverImage?.url ? (
            <img
              className="rounded-lg w-full lg:max-w-md h-60 object-cover"
              src={campaignData.coverImage.url}
              alt=""
            />
          ) : (
            <div className="flex justify-center items-center border border-border rounded-lg w-full lg:max-w-md h-60">
              <AddImageIcon />
            </div>
          )}

          <div className="flex justify-between items-start w-full min-w-0">
            <div className="flex flex-col justify-between w-full h-full min-w-0">
              <div className="flex flex-col gap-6 w-full min-w-0">
                <div className="flex flex-wrap items-center gap-4 w-full">
                  {campaignData?.campaignStatus.status ===
                    CampaignStatus.live && <ActiveCampaignBadge />}

                  <div className="flex flex-wrap items-center gap-4 max-w-full">
                    <div className="flex items-center gap-1 px-4 py-2 border border-border rounded-full max-w-full">
                      <ClockIcon />
                      <p
                        className="font-semibold text-xs sm:text-base truncate"
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                        dangerouslySetInnerHTML={{
                          __html:
                            startDate && endDate
                              ? `${formatDate(startDate.toISOString())} - ${formatDate(endDate.toISOString())}`
                              : "",
                        }}
                      />
                    </div>
                  </div>
                </div>

                <h1 className="w-full font-semibold text-2xl md:text-3xl text-primary break-words">
                  {campaignData?.name}
                </h1>
              </div>
              {/* <div className="flex flex-col gap-3">
								<div className="flex justify-between items-center w-full">
									<span className="font-semibold text-base text-secondary">
										Budget used:
									</span>
									<span className="font-semibold text-primary text-xl">
										₹ {millify(campaignData?.metrics?.budgetUsed || 0)} of{" "}
										{millify(campaignData?.budget || 0)}
									</span>
								</div>

								<Slider
									stepsVisible={false}
									thumbWidth="30"
									thumbHeight="30"
									trackHeight="h-6"
									max={campaignData?.budget || 0}
									value={[campaignData?.metrics?.budgetUsed || 0]}
								/>
							</div> */}
            </div>
          </div>
        </div>
      )}
      {fetchingCampaignMetrics ? (
        <div className="flex justify-center items-center w-full h-96">
          <Spinner />
        </div>
      ) : (
        campaignMetrics && (
          <div className="flex flex-col gap-8">
            <StatsHomeDashboardBrandPartial
              campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
            />

            <Barchart
              fetchingCampaignMetrics={fetchingCampaignMetrics}
              campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
              chartData={ApprovedPostsChartData}
              title="Approved posts"
            />
            <Linechart
              campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
              fetchingCampaignMetrics={fetchingCampaignMetrics}
              lineData={campaignMetrics.postsPerDay}
              chartData={PostPerDayChartData}
              title="Posts per day"
            />
            <Linechart
              campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
              fetchingCampaignMetrics={fetchingCampaignMetrics}
              lineData={campaignMetrics.totalViewsChart}
              chartData={TotalViewsChartData}
              title="Total views"
            />
            <Barchart
              fetchingCampaignMetrics={fetchingCampaignMetrics}
              campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
              chartData={AverageViewsPerPostChartData}
              title="Average views per post"
            />
          </div>
        )
      )}
      <Outlet />
    </div>
  );
};
